/**
 * Aba "Tempo" da barra lateral: as estações meteorológicas do INMET e os
 * aeródromos (METAR).
 *
 * O painel escolhe o que o mapa pinta (temperatura, umidade, vento, chuva,
 * pressão ou orvalho), mostra a legenda dessa variável e responde, sobre o
 * quadro exibido, as perguntas de relance: quantas estações mediram, onde
 * está o extremo e em que aeródromo há fumaça agora.
 */
import clsx from 'clsx';
import { CircleQuestionMark, ExternalLink, RefreshCw, Settings2, Thermometer } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatInt } from '../lib/format';
import { formatRelative, formatTimeBrt } from '../lib/time';
import { shownTimeOf, useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { Button, Dialog, Hint, IconButton, Section, Slider, Toggle } from '../ui/primitives';
import { BASE_METEO } from './api';
import { ESCALAS, VARIAVEIS, comUnidade } from './escalas';
import { avisoDeFrescor } from './frescor';
import { resumoNoQuadro } from './leitura';
import { SimboloEstacao } from './MeteoPopup';
import { temFumaca, visibilidadeTexto } from './metar';
import { LIMITES, useMeteoStore } from './store';
import { carregarMeteo } from './sync';
import type { Variavel } from './types';

/** Escolha da variável: grade de botões (seis cabem em duas linhas de três). */
function SeletorVariavel() {
  const variavel = useMeteoStore((s) => s.estilo.variavel);
  const setEstilo = useMeteoStore((s) => s.setEstilo);
  return (
    <div>
      <div id="meteo-variavel" className="mb-1 text-xs text-ink-300">
        No mapa
      </div>
      <div role="radiogroup" aria-labelledby="meteo-variavel" className="grid grid-cols-3 gap-1">
        {VARIAVEIS.map((v) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={variavel === v}
            onClick={() => setEstilo({ variavel: v })}
            className={clsx(
              'min-h-8 rounded-md border px-1.5 text-xs font-medium transition-colors pointer-coarse:min-h-11',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400',
              variavel === v ? 'border-rio-400/60 bg-rio-400/15 text-white' : 'border-ink-600 bg-ink-800 text-ink-300 hover:text-ink-100',
            )}
          >
            {ESCALAS[v].curto}
          </button>
        ))}
      </div>
    </div>
  );
}

function Legenda({ variavel }: { variavel: Variavel }) {
  const e = ESCALAS[variavel];
  return (
    <div className="space-y-2">
      <div>
        <div className="mb-0.5 flex items-baseline justify-between text-[11px] text-ink-300">
          <span>{e.nome}</span>
          <span className="text-ink-400">{e.unidade}</span>
        </div>
        <div className="flex gap-0.5">
          {e.faixas.map((f) => (
            <div key={f.rotulo} className="min-w-0 flex-1" title={`${f.rotulo} ${e.unidade}`}>
              <div className="h-2.5 rounded-[2px]" style={{ background: f.cor }} />
            </div>
          ))}
        </div>
        <div className="mt-0.5 flex gap-0.5 text-left text-[8.5px] leading-tight text-ink-400 tabular-nums">
          {e.faixas.map((f, i) => (
            <span key={f.rotulo} className="min-w-0 flex-1 truncate">
              {i === 0 ? '' : Number.isFinite(f.min) ? f.min : ''}
            </span>
          ))}
        </div>
        <div className="mt-0.5 flex justify-between text-[9px] text-ink-400">
          <span>{e.extremos[0]}</span>
          <span>{e.extremos[1]}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-300">
        <span className="inline-flex items-center gap-1">
          <SimboloEstacao rede="i" cor="#e9eef2" tamanho={12} /> estação do INMET
        </span>
        <span className="inline-flex items-center gap-1">
          <SimboloEstacao rede="m" cor="#e9eef2" tamanho={12} /> aeródromo (METAR)
        </span>
        <span className="inline-flex items-center gap-1">
          <SimboloEstacao rede="i" cor={null} tamanho={12} /> sem observação
        </span>
        <span className="inline-flex items-center gap-1">
          <svg viewBox="0 0 12 12" width={12} height={12} aria-hidden className="shrink-0">
            <path d="M6 11 V2 M3.5 4.8 L6 2 L8.5 4.8" fill="none" stroke="#ecf2f8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          vento (para onde vai)
        </span>
      </div>
    </div>
  );
}

function AparenciaDialog(props: { open: boolean; onClose: () => void }) {
  const estilo = useMeteoStore((s) => s.estilo);
  const setEstilo = useMeteoStore((s) => s.setEstilo);
  return (
    <Dialog title="Aparência das estações" open={props.open} onClose={props.onClose}>
      <div className="space-y-4 p-4">
        <Slider label="Tamanho" value={estilo.tamanho} {...LIMITES.tamanho} onChange={(tamanho) => setEstilo({ tamanho })} />
        <Slider
          label="Opacidade"
          value={estilo.opacidade}
          {...LIMITES.opacidade}
          onChange={(opacidade) => setEstilo({ opacidade })}
          format={(v) => `${Math.round(v * 100)}%`}
        />
        <Toggle
          label="Mostrar a seta do vento"
          checked={estilo.mostrarVento}
          onChange={(mostrarVento) => setEstilo({ mostrarVento })}
          description="Aponta para onde o vento vai; mais comprida quanto mais forte."
        />
        <Toggle
          label="Mostrar os aeródromos (METAR)"
          checked={estilo.mostrarMetar}
          onChange={(mostrarMetar) => setEstilo({ mostrarMetar })}
          description="Losangos: observação de hora em hora dos aeroportos, com visibilidade e fumaça."
        />
      </div>
    </Dialog>
  );
}

export function MeteoPanel() {
  const visivel = useMeteoStore((s) => s.visivel);
  const situacao = useMeteoStore((s) => s.situacao);
  const dados = useMeteoStore((s) => s.dados);
  const estilo = useMeteoStore((s) => s.estilo);
  const setVisivel = useMeteoStore((s) => s.setVisivel);
  const instante = useTimeStore((s) => shownTimeOf(s));
  const agora = useNow(60_000);
  const [aparenciaAberta, setAparenciaAberta] = useState(false);

  const aviso = avisoDeFrescor(dados.gerado, dados.maisNova, agora);
  const resumo = useMemo(
    () => resumoNoQuadro(dados, estilo.variavel, instante, estilo.mostrarMetar, temFumaca),
    [dados, estilo.variavel, estilo.mostrarMetar, instante],
  );
  const nInmet = useMemo(() => dados.estacoes.filter((e) => e.rede === 'i').length, [dados.estacoes]);
  const nMetar = dados.estacoes.length - nInmet;
  const escala = ESCALAS[estilo.variavel];
  const nomeExtremo = (e: { nome: string; uf: string | null }) => (e.uf ? `${e.nome}/${e.uf}` : e.nome);

  if (!BASE_METEO) {
    return (
      <Section title="Estações meteorológicas" icon={Thermometer}>
        <Hint>A camada das estações meteorológicas está desligada nesta versão.</Hint>
      </Section>
    );
  }

  return (
    <div>
      <Section
        title="Estações meteorológicas"
        icon={Thermometer}
        right={
          visivel && (
            <IconButton
              icon={Settings2}
              size="sm"
              label="Aparência das estações"
              onClick={() => setAparenciaAberta(true)}
              className="pointer-coarse:size-11"
            />
          )
        }
      >
        <Toggle
          label="Estações do INMET e aeródromos"
          checked={visivel}
          onChange={setVisivel}
          description="Temperatura, umidade, vento, chuva e pressão medidos de hora em hora, animados junto com o satélite."
        />

        {visivel && aviso && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            {aviso}
          </div>
        )}
        {visivel && situacao.estado === 'error' && dados.estacoes.length === 0 && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            Não deu para baixar as estações: {situacao.erro}
          </div>
        )}
        {visivel && situacao.estado === 'loading' && <div className="text-xs text-ink-300">Baixando as estações…</div>}

        {visivel && dados.estacoes.length > 0 && (
          <>
            <SeletorVariavel />
            <Legenda variavel={estilo.variavel} />

            <div className="space-y-1 rounded-lg border border-ink-700/70 bg-ink-800/50 px-2.5 py-1.5 text-xs leading-snug text-ink-200">
              <div>
                {instante !== null && <span className="mr-1 font-medium text-white tabular-nums">{formatTimeBrt(instante)}</span>}
                {resumo.comValor === 0
                  ? `Nenhuma estação com ${escala.curto.toLowerCase()} medida neste horário.`
                  : `${formatInt(resumo.comValor)} de ${formatInt(resumo.total)} estações com ${escala.curto.toLowerCase()} medida.`}
              </div>
              {resumo.maior && resumo.menor && resumo.comValor > 1 && (
                <div className="text-ink-300">
                  maior: <span className="text-ink-100">{comUnidade(estilo.variavel, resumo.maior.valor)}</span> em{' '}
                  {nomeExtremo(resumo.maior.estacao)} · menor:{' '}
                  <span className="text-ink-100">{comUnidade(estilo.variavel, resumo.menor.valor)}</span> em{' '}
                  {nomeExtremo(resumo.menor.estacao)}
                </div>
              )}
              {resumo.fumaca.length > 0 && (
                <div className="text-fogo-400">
                  Fumaça ou névoa seca no METAR:{' '}
                  {resumo.fumaca
                    .map(({ estacao, leitura }) => {
                      const vis = visibilidadeTexto(leitura.valores.vis);
                      return vis ? `${estacao.nome} (${vis})` : estacao.nome;
                    })
                    .join(', ')}
                </div>
              )}
            </div>

            <Hint>Toque numa estação para ver as últimas 48 h: temperatura, umidade, vento, pressão e chuva.</Hint>
          </>
        )}
      </Section>

      <Section title="Dados" icon={RefreshCw} defaultOpen={false} storageKey="meteo-dados">
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao atualizar: {situacao.erro}</div>}
          {dados.estacoes.length > 0 && (
            <div>
              {formatInt(nInmet)} estações do INMET e {formatInt(nMetar)} aeródromos
              {dados.maisNova !== null && <> · observação mais nova {formatRelative(dados.maisNova, agora)}</>}
              {dados.gerado !== null && <> · espelho atualizado {formatRelative(dados.gerado, agora)}</>}
            </div>
          )}
          <Button icon={RefreshCw} onClick={() => void carregarMeteo(true)}>
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false} storageKey="meteo-oque">
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            <strong>Estações do INMET</strong> (quadrados): as automáticas medem temperatura, umidade, vento, pressão e
            chuva e transmitem de hora em hora; as convencionais e as sinóticas de aeroporto, algumas vezes por dia. O
            INMET publica cada hora cerca de uma hora depois, pela rede WIS2 da Organização Meteorológica Mundial.
          </p>
          <p>
            <strong>Aeródromos</strong> (losangos): o METAR é a observação que o aeroporto faz para a aviação, de hora em
            hora (e a qualquer momento, no SPECI, quando o tempo muda). Traz a visibilidade e o tempo presente — é ali que
            aparece <em>FU</em>, fumaça, e <em>HZ</em>, névoa seca, observados por gente, na hora.
          </p>
          <p>
            O mapa mostra a observação da hora do quadro: a das 14:00 vale para o quadro das 14:40, nunca a das 15:00. Com
            mais de 1 h de idade ela aparece esmaecida; com mais de 2 h, a estação fica vazada. Estação calada não é zero.
          </p>
          <p>
            Recorte: a Amazônia Legal e uma faixa de 100 km em volta. As últimas 48 h.
          </p>
          <p className="text-ink-400">
            Fontes: INMET — rede de estações meteorológicas, pelo nó WIS2 da OMM;{' '}
            <a href="https://portal.inmet.gov.br/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
              portal.inmet.gov.br
              <ExternalLink size={10} />
            </a>
            . NOAA Aviation Weather Center — METAR;{' '}
            <a href="https://aviationweather.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-rio-300 hover:underline">
              aviationweather.gov
              <ExternalLink size={10} />
            </a>
            .
          </p>
          <p className="text-ink-400">Dado bruto da rede, sem controle de qualidade posterior. Não serve para a navegação aérea.</p>
        </div>
      </Section>

      <AparenciaDialog open={aparenciaAberta} onClose={() => setAparenciaAberta(false)} />
    </div>
  );
}
